import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Lock, Crown, Download, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Modeles() {
  const { user } = useAuth();
  const [data, setData] = useState({ is_premium: false, templates: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/templates");
        setData(data);
      } catch {
        toast.error("Chargement impossible");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const download = async (id, title) => {
    try {
      const resp = await api.get(`/templates/${id}/download`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${title} téléchargé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Téléchargement impossible");
    }
  };

  const isPremium = data.is_premium || user?.is_premium;

  return (
    <div className="space-y-10" data-testid="modeles-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Pack premium</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Modèles & lettres</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Bail commenté, état des lieux, lettres CAF, garant, recours préfecture… Des modèles écrits par des juristes,
          prêts à signer et à envoyer. {isPremium ? "Téléchargements illimités." : "Réservé aux abonnés Premium."}
        </p>
      </header>

      {!isPremium && (
        <section className="rounded-2xl border-2 border-yellow-200 bg-yellow-50 p-6 sm:p-8 flex flex-col sm:flex-row items-start gap-5" data-testid="modeles-locked-banner">
          <div className="w-12 h-12 rounded-2xl bg-yellow-300 text-slate-900 flex items-center justify-center shrink-0">
            <Crown size={22} />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider font-semibold text-yellow-800">Verrouillé</p>
            <h2 className="font-display text-2xl font-bold text-slate-900 mt-1">Débloque les 8 modèles avec Premium</h2>
            <p className="text-slate-700 mt-2">
              7 jours d'essai gratuits, sans carte bancaire. Annule quand tu veux.
            </p>
            <Link
              to="/premium"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
              data-testid="modeles-go-premium-btn"
            >
              Découvrir Premium <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {loading ? (
        <p className="text-slate-500">Chargement...</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.templates.map((t) => (
            <div
              key={t.id}
              data-testid={`template-${t.id}`}
              className={`rounded-2xl border p-5 transition-all ${
                isPremium ? "bg-white border-slate-200 hover:border-indigo-300" : "bg-slate-50 border-slate-200"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  isPremium ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-400"
                }`}>
                  {isPremium ? <FileText size={20} /> : <Lock size={18} />}
                </div>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                  {t.category}
                </span>
              </div>
              <p className={`font-display font-semibold ${isPremium ? "text-slate-900" : "text-slate-500"}`}>
                {t.title}
              </p>
              <p className="text-sm text-slate-500 mt-1">{t.subtitle}</p>
              {isPremium ? (
                <button
                  onClick={() => download(t.id, t.title)}
                  data-testid={`download-template-${t.id}`}
                  className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-slate-900 text-white text-xs font-medium hover:bg-slate-800"
                >
                  <Download size={12} /> Télécharger
                </button>
              ) : (
                <Link
                  to="/premium"
                  data-testid={`unlock-template-${t.id}`}
                  className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-yellow-100 text-yellow-900 text-xs font-medium hover:bg-yellow-200"
                >
                  <Lock size={12} /> Déverrouiller
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
